import styled from "styled-components";
import {
  CommonEditMessage,
  CommonMessage,
  CommonMomentEvent,
  EventStreamSettings,
  FunctionCallResult,
  Source,
} from "../../types";
import { ReactNode } from "react";
import { ColorAA } from "../../../../components/src/constants";
import ContentMessage, { EditContentMessage } from "./ContentMessage";
import RawMessage from "./RawMessage";
import KVMessage, { EditKVMessage } from "./KVMessage";
import FactEventMessage from "./FactEventMessage";
import FnCallMessage from "./FnCallMessage";
import PuppeteerMessage from "./PuppeteerMessage";
import KVDelTreeMessage, { EditKVDelTreeMessage } from "./KVDelTreeMessage";

export const EventBody = styled.span`
  font-family: monospace;

  &,
  input,
  select,
  textarea {
    font-size: 14px !important;
  }
`;

export const EventContent = styled.span<{ $showWhitespace?: boolean }>`
  white-space: ${({ $showWhitespace }) =>
    $showWhitespace ? "pre-wrap" : "normal"};
`;

const EventTableRow = styled.tr<{ $backgroundColor?: string }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border: 5px solid white;

  > td {
    padding: 5px;
  }
`;

function DisplaySource({ source }: { source: Source }) {
  if (source === "user") {
    return <i className="bi-controller" />;
  } else if (source === "assistant") {
    return <i className="bi-robot" />;
  } else if (source === "system") {
    return <i className="bi-braces" />;
  } else if (source === "function") {
    return <i className="bi-exclamation" />;
  }
  source satisfies never;
}

function DisplayFnResultIcon({
  resultType,
}: {
  resultType: FunctionCallResult;
}) {
  if (resultType === "success") {
    return <i className="bi-check" style={{ color: "green" }} />;
  } else if (resultType === "failure") {
    return <i className="bi-x" style={{ color: "red" }} />;
  } else if (resultType === "warning") {
    return <i className="bi-exclamation" style={{ color: "orange" }} />;
  } else if (resultType === "update") {
    return <></>;
  }
  resultType satisfies never;
}

function HighlightableEventRow({
  children,
  fnCallId,
  highlightedFnCallId,
  onHighlightFnCallId,
}: {
  children: ReactNode;
  fnCallId: string | null;
  highlightedFnCallId: string | null;
  onHighlightFnCallId: (fnCallId: string | null) => void;
}) {
  const isHighlighted = highlightedFnCallId && highlightedFnCallId === fnCallId;
  const background = isHighlighted ? "#cde" : ColorAA.lightBeige;

  return (
    <EventTableRow
      $backgroundColor={background}
      onMouseEnter={() => fnCallId && onHighlightFnCallId(fnCallId)}
      onMouseLeave={() => onHighlightFnCallId(null)}
    >
      {children}
    </EventTableRow>
  );
}

export interface MessageComponentProps<T extends CommonMessage> {
  message: T;
  relatedMessages?: Array<T>;
  displaySettings?: EventStreamSettings;
}

export interface EditMessageComponentProps<T extends CommonMessage> {
  message: CommonEditMessage<T>;
  onGameEventMessageChange: (message: CommonEditMessage<T>) => void;
}

export type EditMessageComponent<T extends CommonMessage> = React.FC<
  EditMessageComponentProps<T>
>;

export type MessageComponent<T extends CommonMessage> = React.FC<
  MessageComponentProps<T>
>;

export const getMessageComponent = (
  messageType?: string
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
MessageComponent<any> => {
  switch (messageType) {
    case "ContentMessage":
    case "ContentEventMessage":
    case "ImportantContentEventMessage":
      return ContentMessage;
    case "KVMessage":
      return KVMessage;
    case "KVDelTreeMessage":
      return KVDelTreeMessage;
    case "FnCallMessage":
    case "FnCallEventMessage":
      return FnCallMessage;
    case "FactEventMessage":
      return FactEventMessage;
    case "PuppeteerMessage":
      return PuppeteerMessage;
    default:
      return RawMessage;
  }
};

export const getEditMessageComponent = (
  messageType?: string
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
EditMessageComponent<any> => {
  switch (messageType) {
    case "ContentMessage":
      return EditContentMessage;
    case "KVMessage":
      return EditKVMessage;
    case "KVDelTreeMessage":
      return EditKVDelTreeMessage;
    default:
      // Show a read-only component for events with unsupported types
      return getMessageComponent(messageType);
  }
};

export function EventRow({
  event,
  prevEvent,
  highlightedFnCallId,
  onHighlightFnCallId,
  displaySettings,
}: {
  event: CommonMomentEvent;
  prevEvent?: CommonMomentEvent;
  highlightedFnCallId: string | null;
  onHighlightFnCallId: (fnCallId: string | null) => void;
  displaySettings: EventStreamSettings;
}) {
  const message = event.message;

  const Message = getMessageComponent(message.message_type);
  const fnCallId: string | null = null;
  const resultType: FunctionCallResult | null = null;
  const sender = message.sender;

  return (
    <HighlightableEventRow
      fnCallId={fnCallId}
      highlightedFnCallId={highlightedFnCallId}
      onHighlightFnCallId={onHighlightFnCallId}
    >
      <td align="right" className="pe-2">
        <DisplaySource source={event.message.source} />
      </td>
      <td width="120px">
        <EventBody>{sender === null ? "(no sender)" : sender}</EventBody>
      </td>
      <td width="150px">
        <EventBody>{event.message.message_type}</EventBody>
      </td>
      <td>{resultType && <DisplayFnResultIcon resultType={resultType} />}</td>
      <td colSpan={message.message_type === "FnCallMessage" ? 1 : 2}>
        <EventContent $showWhitespace={displaySettings.showMessageWhitespace}>
          <Message
            message={message}
            relatedMessages={[prevEvent?.message]}
            displaySettings={displaySettings}
          />
        </EventContent>
      </td>
    </HighlightableEventRow>
  );
}
